// Phone number validation system with international standards
// Based on ITU-T E.164 and national numbering plans

export interface PhoneNumberFormat {
  countryCode: string;
  nationalPrefix?: string;
  nationalNumberLength: number;
  mobilePrefixes: string[];
  operators: Record<string, string>;
  format: string;
  example: string;
}

export interface CountryPhoneConfig {
  isoCode: string;
  countryCode: string;
  nationalPrefix?: string;
  formats: {
    mobile: PhoneNumberFormat;
    landline?: PhoneNumberFormat;
  };
  validation: {
    minLength: number;
    maxLength: number;
    mobilePrefixes: string[];
    landlinePrefixes?: string[];
  };
}

// Comprehensive phone number configurations for different countries
export const countryPhoneNumbers: Record<string, CountryPhoneConfig> = {
  SN: {
    isoCode: 'SN',
    countryCode: '221',
    nationalPrefix: '',
    formats: {
      mobile: {
        countryCode: '221',
        nationalNumberLength: 9,
        mobilePrefixes: ['70', '71', '72', '73', '74', '75', '76', '77', '78', '79'],
        operators: {
          '70': 'Expresso',
          '71': 'Expresso',
          '72': 'Expresso',
          '73': 'Expresso',
          '74': 'Expresso',
          '75': 'Expresso',
          '76': 'Orange',
          '77': 'Orange',
          '78': 'Orange',
          '79': 'Orange'
        },
        format: 'XX XXX XX XX',
        example: '77 123 45 67'
      }
    },
    validation: {
      minLength: 9,
      maxLength: 9,
      mobilePrefixes: ['70', '71', '72', '73', '74', '75', '76', '77', '78', '79']
    }
  },
  ML: {
    isoCode: 'ML',
    countryCode: '223',
    nationalPrefix: '',
    formats: {
      mobile: {
        countryCode: '223',
        nationalNumberLength: 8,
        mobilePrefixes: ['66', '67', '68', '69', '70', '71', '72', '73', '74', '75', '76', '77', '78', '79', '90', '91', '92', '93', '94', '95', '96', '97', '98', '99'],
        operators: {
          '66': 'Malitel',
          '67': 'Malitel',
          '68': 'Malitel',
          '69': 'Malitel',
          '70': 'Orange',
          '71': 'Orange',
          '72': 'Orange',
          '73': 'Orange',
          '74': 'Orange',
          '75': 'Orange',
          '76': 'Orange',
          '77': 'Orange',
          '78': 'Orange',
          '79': 'Orange',
          '90': 'Malitel',
          '91': 'Malitel',
          '92': 'Malitel',
          '93': 'Malitel',
          '94': 'Malitel',
          '95': 'Malitel',
          '96': 'Malitel',
          '97': 'Malitel',
          '98': 'Malitel',
          '99': 'Malitel'
        },
        format: 'XX XXX XXX',
        example: '70 12 34 56'
      }
    },
    validation: {
      minLength: 8,
      maxLength: 8,
      mobilePrefixes: ['66', '67', '68', '69', '70', '71', '72', '73', '74', '75', '76', '77', '78', '79', '90', '91', '92', '93', '94', '95', '96', '97', '98', '99']
    }
  },
  BF: {
    isoCode: 'BF',
    countryCode: '226',
    nationalPrefix: '',
    formats: {
      mobile: {
        countryCode: '226',
        nationalNumberLength: 8,
        mobilePrefixes: ['01', '02', '06', '07', '51', '52', '53', '54', '55', '56', '57', '58', '59', '60', '61', '62', '63', '64', '65', '66', '67', '68', '69', '70', '71', '72', '73', '74', '75', '76', '77', '78', '79'],
        operators: {
          '01': 'Moov',
          '02': 'Moov',
          '06': 'Orange',
          '07': 'Orange',
          '51': 'Moov',
          '52': 'Moov',
          '53': 'Moov',
          '54': 'Orange',
          '55': 'Orange',
          '56': 'Orange',
          '57': 'Orange',
          '58': 'Moov',
          '59': 'Moov',
          '60': 'Moov',
          '61': 'Moov',
          '62': 'Moov',
          '63': 'Moov',
          '64': 'Orange',
          '65': 'Orange',
          '66': 'Orange',
          '67': 'Orange',
          '68': 'Moov',
          '69': 'Moov',
          '70': 'Moov',
          '71': 'Moov',
          '72': 'Moov',
          '73': 'Moov',
          '74': 'Orange',
          '75': 'Orange',
          '76': 'Orange',
          '77': 'Orange',
          '78': 'Telecel',
          '79': 'Moov'
        },
        format: 'XX XXX XXX',
        example: '70 12 34 56'
      }
    },
    validation: {
      minLength: 8,
      maxLength: 8,
      mobilePrefixes: ['01', '02', '06', '07', '51', '52', '53', '54', '55', '56', '57', '58', '59', '60', '61', '62', '63', '64', '65', '66', '67', '68', '69', '70', '71', '72', '73', '74', '75', '76', '77', '78', '79']
    }
  },
  CI: {
    isoCode: 'CI',
    countryCode: '225',
    nationalPrefix: '',
    formats: {
      mobile: {
        countryCode: '225',
        nationalNumberLength: 10,
        mobilePrefixes: ['01', '02', '03', '04', '05', '06', '07', '08', '09', '40', '41', '42', '43', '44', '45', '46', '47', '48', '49', '50', '51', '52', '53', '54', '55', '56', '57', '58', '59', '64', '65', '66', '67', '68', '69', '70', '71', '72', '73', '74', '75', '76', '77', '78', '79', '84', '85', '86', '87', '88', '89', '94', '95', '96', '97', '98'],
        operators: {
          '01': 'Moov',
          '02': 'Moov',
          '03': 'Moov',
          '04': 'MTN',
          '05': 'MTN',
          '06': 'MTN',
          '07': 'Orange',
          '08': 'Orange',
          '09': 'Orange',
          '40': 'Moov',
          '41': 'Moov',
          '42': 'Moov',
          '43': 'Moov',
          '44': 'MTN',
          '45': 'MTN',
          '46': 'MTN',
          '47': 'Orange',
          '48': 'Orange',
          '49': 'Orange',
          '50': 'Moov',
          '51': 'Moov',
          '52': 'Moov',
          '53': 'Moov',
          '54': 'MTN',
          '55': 'MTN',
          '56': 'MTN',
          '57': 'Orange',
          '58': 'Orange',
          '59': 'Orange',
          '64': 'MTN',
          '65': 'MTN',
          '66': 'MTN',
          '67': 'Orange',
          '68': 'Orange',
          '69': 'Orange',
          '70': 'Moov',
          '71': 'Moov',
          '72': 'Moov',
          '73': 'Moov',
          '74': 'MTN',
          '75': 'MTN',
          '76': 'MTN',
          '77': 'Orange',
          '78': 'Orange',
          '79': 'Orange',
          '84': 'MTN',
          '85': 'MTN',
          '86': 'MTN',
          '87': 'Orange',
          '88': 'Orange',
          '89': 'Orange',
          '94': 'MTN',
          '95': 'MTN',
          '96': 'MTN',
          '97': 'Orange',
          '98': 'Orange'
        },
        format: 'XX XXX XXX XX',
        example: '07 12 34 56 78'
      }
    },
    validation: {
      minLength: 10,
      maxLength: 10,
      mobilePrefixes: ['01', '02', '03', '04', '05', '06', '07', '08', '09', '40', '41', '42', '43', '44', '45', '46', '47', '48', '49', '50', '51', '52', '53', '54', '55', '56', '57', '58', '59', '64', '65', '66', '67', '68', '69', '70', '71', '72', '73', '74', '75', '76', '77', '78', '79', '84', '85', '86', '87', '88', '89', '94', '95', '96', '97', '98']
    }
  },
  BJ: {
    isoCode: 'BJ',
    countryCode: '229',
    nationalPrefix: '',
    formats: {
      mobile: {
        countryCode: '229',
        nationalNumberLength: 8,
        mobilePrefixes: ['51', '52', '53', '54', '61', '62', '66', '67', '69', '90', '91', '96', '97'],
        operators: {
          '51': 'MTN',
          '52': 'MTN',
          '53': 'MTN',
          '54': 'MTN',
          '61': 'MTN',
          '62': 'MTN',
          '66': 'MTN',
          '67': 'MTN',
          '69': 'MTN',
          '90': 'MTN',
          '91': 'MTN',
          '96': 'MTN',
          '97': 'MTN'
        },
        format: 'XX XXX XXX',
        example: '90 12 34 56'
      }
    },
    validation: {
      minLength: 8,
      maxLength: 8,
      mobilePrefixes: ['51', '52', '53', '54', '61', '62', '66', '67', '69', '90', '91', '96', '97']
    }
  },
  NE: {
    isoCode: 'NE',
    countryCode: '227',
    nationalPrefix: '',
    formats: {
      mobile: {
        countryCode: '227',
        nationalNumberLength: 8,
        mobilePrefixes: ['70', '74', '80', '88', '89', '90', '91', '92', '94', '96', '97', '98', '99'],
        operators: {
          '70': 'Orange',
          '74': 'Moov',
          '80': 'Orange',
          '88': 'Airtel',
          '89': 'Airtel',
          '90': 'Orange',
          '91': 'Orange',
          '92': 'Orange',
          '94': 'Moov',
          '96': 'Airtel',
          '97': 'Airtel',
          '98': 'Airtel',
          '99': 'Airtel'
        },
        format: 'XX XXX XXX',
        example: '90 12 34 56'
      }
    },
    validation: {
      minLength: 8,
      maxLength: 8,
      mobilePrefixes: ['70', '74', '80', '88', '89', '90', '91', '92', '94', '96', '97', '98', '99']
    }
  },
  GW: {
    isoCode: 'GW',
    countryCode: '245',
    nationalPrefix: '',
    formats: {
      mobile: {
        countryCode: '245',
        nationalNumberLength: 9,
        mobilePrefixes: ['44', '95', '96', '97'],
        operators: {
          '44': 'Guiné Telecom',
          '95': 'Orange',
          '96': 'Orange',
          '97': 'Orange'
        },
        format: 'XX XXX XXX X',
        example: '95 123 456 7'
      }
    },
    validation: {
      minLength: 9,
      maxLength: 9,
      mobilePrefixes: ['44', '95', '96', '97']
    }
  },
  // Ajout de nouveaux pays avec leurs plans de numérotation
  TG: {
    isoCode: 'TG',
    countryCode: '228',
    nationalPrefix: '',
    formats: {
      mobile: {
        countryCode: '228',
        nationalNumberLength: 8,
        mobilePrefixes: ['70', '71', '72', '73', '74', '75', '76', '77', '78', '79', '90', '91', '92', '93', '94', '95', '96', '97', '98', '99'],
        operators: {
          '70': 'Moov',
          '71': 'Moov',
          '72': 'Moov',
          '73': 'Moov',
          '74': 'Moov',
          '75': 'Moov',
          '76': 'Moov',
          '77': 'Moov',
          '78': 'Moov',
          '79': 'Moov',
          '90': 'Togocel',
          '91': 'Togocel',
          '92': 'Togocel',
          '93': 'Togocel',
          '94': 'Togocel',
          '95': 'Togocel',
          '96': 'Togocel',
          '97': 'Togocel',
          '98': 'Togocel',
          '99': 'Togocel'
        },
        format: 'XX XXX XXX',
        example: '90 12 34 56'
      }
    },
    validation: {
      minLength: 8,
      maxLength: 8,
      mobilePrefixes: ['70', '71', '72', '73', '74', '75', '76', '77', '78', '79', '90', '91', '92', '93', '94', '95', '96', '97', '98', '99']
    }
  },
  GN: {
    isoCode: 'GN',
    countryCode: '224',
    nationalPrefix: '',
    formats: {
      mobile: {
        countryCode: '224',
        nationalNumberLength: 9,
        mobilePrefixes: ['60', '61', '62', '63', '64', '65', '66', '67', '68', '69', '70', '71', '72', '73', '74', '75', '76', '77', '78', '79', '90', '91', '92', '93', '94', '95', '96', '97', '98', '99'],
        operators: {
          '60': 'Orange',
          '61': 'Orange',
          '62': 'Orange',
          '63': 'Orange',
          '64': 'Orange',
          '65': 'Orange',
          '66': 'Orange',
          '67': 'Orange',
          '68': 'Orange',
          '69': 'Orange',
          '70': 'Orange',
          '71': 'Orange',
          '72': 'Orange',
          '73': 'Orange',
          '74': 'Orange',
          '75': 'Orange',
          '76': 'Orange',
          '77': 'Orange',
          '78': 'Orange',
          '79': 'Orange',
          '90': 'MTN',
          '91': 'MTN',
          '92': 'MTN',
          '93': 'MTN',
          '94': 'MTN',
          '95': 'MTN',
          '96': 'MTN',
          '97': 'MTN',
          '98': 'MTN',
          '99': 'MTN'
        },
        format: 'XX XXX XXX X',
        example: '60 12 34 56 7'
      }
    },
    validation: {
      minLength: 9,
      maxLength: 9,
      mobilePrefixes: ['60', '61', '62', '63', '64', '65', '66', '67', '68', '69', '70', '71', '72', '73', '74', '75', '76', '77', '78', '79', '90', '91', '92', '93', '94', '95', '96', '97', '98', '99']
    }
  }
};

/**
 * Normalize phone number by removing all non-digit characters
 */
export const normalizePhoneNumber = (phone: string): string => {
  return phone.replace(/[\D\s\-\(\)\.]/g, '');
};

/**
 * Extract country code from phone number
 */
export const extractCountryCode = (phone: string): string | null => {
  const normalized = normalizePhoneNumber(phone);
  
  // Check for common country codes
  const countryCodes = ['221', '222', '223', '224', '225', '226', '227', '228', '229', '230', '231', '232', '233', '234', '235', '236', '237', '238', '239', '240', '241', '242', '243', '244', '245', '246', '247', '248', '249', '250', '251', '252', '253', '254', '255', '256', '257', '258', '259', '260', '261', '262', '263', '264', '265', '266', '267', '268', '269', '270', '271', '272', '273', '274', '275', '276', '277', '278', '279', '280', '281', '282', '283', '284', '285', '286', '287', '288', '289', '290', '291', '292', '293', '294', '295', '296', '297', '298', '299'];
  
  for (const code of countryCodes) {
    if (normalized.startsWith(code)) {
      return code;
    }
  }
  
  return null;
};

/**
 * Get national number from phone number
 */
export const getNationalNumber = (phone: string, countryCode?: string): string => {
  const normalized = normalizePhoneNumber(phone);
  
  if (countryCode) {
    return normalized.replace(new RegExp(`^${countryCode}`), '');
  }
  
  const extractedCode = extractCountryCode(phone);
  if (extractedCode) {
    return normalized.replace(new RegExp(`^${extractedCode}`), '');
  }
  
  return normalized;
};

/**
 * Enhanced phone number validation with international standards
 */
export const phoneValidity = (isoCountryCode: string, phone: string): boolean => {
  const countryConfig = countryPhoneNumbers[isoCountryCode];
  
  if (!countryConfig) {
    return false;
  }
  
  const normalized = normalizePhoneNumber(phone);
  const nationalNumber = getNationalNumber(phone, countryConfig.countryCode);
  
  // Check length
  if (nationalNumber.length < countryConfig.validation.minLength || 
      nationalNumber.length > countryConfig.validation.maxLength) {
    return false;
  }
  
  // Check mobile prefixes
  const prefix = nationalNumber.substring(0, 2);
  return countryConfig.validation.mobilePrefixes.includes(prefix);
};

/**
 * Get operator information for a phone number
 */
export const getPhoneOperator = (isoCountryCode: string, phone: string): string | undefined => {
  const countryConfig = countryPhoneNumbers[isoCountryCode];
  
  if (!countryConfig) {
    return undefined;
  }
  
  const nationalNumber = getNationalNumber(phone, countryConfig.countryCode);
  const prefix = nationalNumber.substring(0, 2);
  
  return countryConfig.formats.mobile.operators[prefix];
};

/**
 * Format phone number according to country format
 */
export const formatPhoneNumber = (isoCountryCode: string, phone: string): string => {
  const countryConfig = countryPhoneNumbers[isoCountryCode];
  
  if (!countryConfig) {
    return phone;
  }
  
  const nationalNumber = getNationalNumber(phone, countryConfig.countryCode);
  const format = countryConfig.formats.mobile.format;
  
  let formatted = '';
  let numberIndex = 0;
  
  for (let i = 0; i < format.length && numberIndex < nationalNumber.length; i++) {
    if (format[i] === 'X') {
      formatted += nationalNumber[numberIndex];
      numberIndex++;
    } else {
      formatted += format[i];
    }
  }
  
  return formatted;
};

/**
 * Validate phone number with detailed error information
 */
export const validatePhoneNumber = (isoCountryCode: string, phone: string): {
  isValid: boolean;
  error?: string;
  operator?: string;
  formattedNumber?: string;
} => {
  const countryConfig = countryPhoneNumbers[isoCountryCode];
  
  if (!countryConfig) {
    return {
      isValid: false,
      error: 'Pays non supporté'
    };
  }
  
  const normalized = normalizePhoneNumber(phone);
  const nationalNumber = getNationalNumber(phone, countryConfig.countryCode);
  
  // Check length
  if (nationalNumber.length < countryConfig.validation.minLength) {
    return {
      isValid: false,
      error: `Le numéro doit contenir au moins ${countryConfig.validation.minLength} chiffres`
    };
  }
  
  if (nationalNumber.length > countryConfig.validation.maxLength) {
    return {
      isValid: false,
      error: `Le numéro ne peut pas dépasser ${countryConfig.validation.maxLength} chiffres`
    };
  }
  
  // Check mobile prefixes
  const prefix = nationalNumber.substring(0, 2);
  if (!countryConfig.validation.mobilePrefixes.includes(prefix)) {
    return {
      isValid: false,
      error: 'Préfixe de numéro mobile invalide'
    };
  }
  
  const operator = getPhoneOperator(isoCountryCode, phone);
  const formattedNumber = formatPhoneNumber(isoCountryCode, phone);
  
  return {
    isValid: true,
    operator,
    formattedNumber
  };
};

// This method is use to validate the OTP code
export const isOTPValid = (code: string) => {
  let isValid = false;

  if (code.replace(/[\D\s-]/g, "").length === 6 && /\d+/.test(code))
    isValid = true;

  return isValid;
};

// This method is use to validate the OTP code
export const isEmailValid = (email: string) => {
  const regex =
    /^(([^<>()[\],;:\s@]+(\.[^<>()[\],;:\s@]+)*)|(.+))@(([^<>()[\],;:\s@]+\.)+[^<>()[\],;:\s@]{2,})$/i;
  if (!email || regex.test(email) === false) {
    return false;
  }
  return true;
};

// This method is use to validate the OTP code
export const isAmountValid = (amount: string) => {
  return parseInt(amount.replace(/[\D\s-]/g, "")) >= 2000;
};

/**
 * Basic client-side validation for an address string.
 * This function checks for:
 * 1. Not empty or whitespace only.
 * 2. Meets a minimum length requirement.
 * 3. Does not contain basic forbidden characters (simple XSS prevention).
 *
 * This does NOT verify if the address exists or is correctly formatted for a specific country.
 * Server-side validation is crucial.
 *
 * @param {string} addressString The address string to validate.
 * @returns {boolean} True if the address string passes these basic checks, false otherwise.
 */
export const isValidAddressString = (addressString) => {
  // Step 1: Check if the string is null, undefined, or empty after trimming whitespace
  if (
    !addressString ||
    typeof addressString !== "string" ||
    addressString.trim().length === 0
  ) {
    return false;
  }

  const trimmedAddress = addressString.trim();

  // Step 2: Check for a reasonable minimum length
  // Adjust this threshold based on your typical shortest valid address (e.g., "Paris" is too short for a full address)
  const MIN_ADDRESS_LENGTH = 4; // Example: "1 Main St, NY" is around 13 chars.
  if (trimmedAddress.length < MIN_ADDRESS_LENGTH) {
    return false;
  }

  // Step 5: Check for potentially malicious characters (basic XSS prevention)
  // This is a very basic check; full XSS prevention requires proper encoding on the backend.
  // Common characters used in XSS attacks: < > " ' `
  const forbiddenCharactersRegex = /[<>"'`]/;
  if (forbiddenCharactersRegex.test(trimmedAddress)) {
    return false;
  }

  // If all specified checks pass
  return true;
};
